import { create } from "zustand";
import { persist } from "zustand/middleware";

// Define trip filter type
export interface TripFilter {
  showAll: boolean;
  visibleTripIds: string[];
  hiddenTripIds: string[];
}

export function tripIsVisible(filter: TripFilter, tripId: string): boolean {
  return filter.showAll
    ? !filter.hiddenTripIds.includes(tripId)
    : filter.visibleTripIds.includes(tripId);
}

interface ShortcutsState {
  // Visibility toggles
  showPickups: boolean;
  showDeliveries: boolean;
  showCarrierDropoffs: boolean;
  showReturns: boolean;
  showDepots: boolean; // Dispatch center locations
  showOperators: boolean;
  showCarrierDropoffsPanel: boolean; // Carrier dropoffs assignment panel

  // Trip filtering
  tripFilter: TripFilter;

  // Selection mode
  isSelectingMode: boolean;

  // Route-manager F4: time-window filter. null = "all windows" (no filter).
  // Special sentinel "overdue" filters to overdue tasks only. Otherwise
  // it's a TimeSlot.id from the server.
  windowFilter: string | null;

  // Actions
  setShowPickups: (show: boolean) => void;
  setShowDeliveries: (show: boolean) => void;
  setShowCarrierDropoffs: (show: boolean) => void;
  setShowReturns: (show: boolean) => void;
  setShowDepots: (show: boolean) => void;
  setShowOperators: (show: boolean) => void;
  setShowCarrierDropoffsPanel: (show: boolean) => void;
  setTripFilter: (filter: TripFilter) => void;
  setIsSelectingMode: (isSelecting: boolean) => void;
  toggleSelectingMode: () => void;
  setWindowFilter: (slotId: string | null) => void;

  // Trip visibility helpers
  toggleAllTrips: () => void;
}

export const useShortcutsStore = create<ShortcutsState>()(
  persist(
    (set) => ({
      // Default values
      showPickups: true,
      showDeliveries: true,
      showCarrierDropoffs: true,
      showReturns: true,
      showDepots: true,
      showOperators: true,
      showCarrierDropoffsPanel: true,
      isSelectingMode: false,
      windowFilter: null,

      tripFilter: {
        showAll: true,
        visibleTripIds: [],
        hiddenTripIds: [],
      },

      // Setters
      setShowPickups: (show) => set({ showPickups: show }),
      setShowDeliveries: (show) => set({ showDeliveries: show }),
      setShowCarrierDropoffs: (show) => set({ showCarrierDropoffs: show }),
      setShowReturns: (show) => set({ showReturns: show }),
      setShowDepots: (show) => set({ showDepots: show }),
      setShowOperators: (show) => set({ showOperators: show }),
      setShowCarrierDropoffsPanel: (show) =>
        set({ showCarrierDropoffsPanel: show }),
      setTripFilter: (filter) => set({ tripFilter: filter }),
      setIsSelectingMode: (isSelecting) =>
        set({ isSelectingMode: isSelecting }),
      setWindowFilter: (slotId) => set({ windowFilter: slotId }),

      // Toggle helpers
      toggleSelectingMode: () =>
        set((state) => ({ isSelectingMode: !state.isSelectingMode })),

      // Trip filter helpers
      toggleAllTrips: () =>
        set((state) => ({
          tripFilter: {
            showAll: !state.tripFilter.showAll,
            visibleTripIds: [],
            hiddenTripIds: [],
          },
        })),
    }),
    {
      name: "shortcuts-storage", // unique name for localStorage key
      partialize: (state) => ({
        // Only persist these fields (ignore functions)
        showPickups: state.showPickups,
        showDeliveries: state.showDeliveries,
        showCarrierDropoffs: state.showCarrierDropoffs,
        showReturns: state.showReturns,
        showDepots: state.showDepots,
        showOperators: state.showOperators,
        showCarrierDropoffsPanel: state.showCarrierDropoffsPanel,
        tripFilter: state.tripFilter,
        isSelectingMode: state.isSelectingMode,
        windowFilter: state.windowFilter,
      }),
    }
  )
);
